import { useState, useEffect, useCallback, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import type { StatsBlock, DocumentsStatsResponse } from "shared/types/renovation";
import { toApiParams, type RenovationDocumentsFilters } from "./useRenovationDocumentsData";

export type { StatsBlock };

/**
 * Fetches server-side document statistics for both the unfiltered universe
 * and the currently filtered set.
 *
 * Mirrors the pattern in useHelpdeskDataLazy.ts:166-197.
 * Re-fetches whenever filterParams change (uses JSON-stringified key as dep).
 *
 * @param buildingId  - When set, scopes the `total` block to this building.
 * @param filters     - Active filters (same shape as useRenovationDocumentsData).
 */
export function useRenovationDocumentsStats(
  buildingId: string | undefined,
  filters: RenovationDocumentsFilters
): {
  total: StatsBlock | null;
  filtered: StatsBlock | null;
  isLoading: boolean;
  error: Error | null;
  /** Re-trigger the stats fetch. Returns a Promise that always resolves (errors are captured in `error` state). */
  refetch: () => Promise<void>;
} {
  const { session } = useSession();

  const [total, setTotal] = useState<StatsBlock | null>(null);
  const [filtered, setFiltered] = useState<StatsBlock | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Build query params from filters (same conversion as data hook)
  const filterParams = useMemo(() => toApiParams(filters), [filters]);
  const filterKey = useMemo(
    () => JSON.stringify(filterParams),
    [filterParams]
  );

  // Endpoint mirrors useRenovationDocumentsData.ts:89-91
  const endpoint = buildingId
    ? `${API_URL}/renovation/building/${buildingId}/documents/stats`
    : `${API_URL}/renovation/documents/stats`;

  const fetchStats = useCallback(async () => {
    if (!session) return;

    setIsLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams(filterParams);
      const url = queryParams.toString()
        ? `${endpoint}?${queryParams}`
        : endpoint;

      const response = await fetchWithAuth(url, { method: "GET" }, session);

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText);
        throw new Error(
          `Fout bij ophalen van statistieken (${response.status}): ${errorText}`
        );
      }

      const data: DocumentsStatsResponse = await response.json();
      setTotal(data.total);
      setFiltered(data.filtered);
    } catch (err) {
      const typedError = err instanceof Error ? err : new Error(String(err));
      // Log to console so Sentry captures the breadcrumb
      console.error("[useRenovationDocumentsStats] Error fetching stats:", typedError);
      setError(typedError);
      toast.error("Fout bij ophalen van documentstatistieken");
      // Do NOT re-throw: this runs inside a useEffect callback, and unhandled
      // rejections from effects cannot be caught by callers.
    } finally {
      setIsLoading(false);
    }
  }, [session, endpoint, filterParams]);

  // Re-fetch when session becomes available or filterKey changes
  useEffect(() => {
    if (!session) return;
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, filterKey, endpoint]);

  return { total, filtered, isLoading, error, refetch: fetchStats };
}
